import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TransportistaOption } from '@opb-workspace/shared';
import { environment } from '../../environments/environment';

export interface CreateUserRequest {
    email: string;
    first_name: string;
    last_name: string;
    phone: string;
    role: string;
    client_id?: number;
    consignee_id?: number;
    /** tblParties.Id del transportista; solo con rol transportista */
    transportista_id?: number | null;
    password: string;
    is_active: boolean;
}

export interface Module {
    id: string;
    code: string;
    name: string;
    description?: string;
    route: string;
    icon?: string;
    order: number;
    is_active: boolean;
}

export interface User {
    id: string;
    username?: string;
    email: string;
    first_name: string;
    last_name: string;
    phone: string;
    role: string;
    client_id?: number;
    consignee_id?: number;
    transportista_id?: number | null;
    is_active: boolean;
    created_at: string;
    updated_at?: string;
    modules?: Module[];
}

export interface Client {
    clienteId: number;
    cliente: string;
}

export interface ApiResponse<T> {
    success: boolean;
    data?: T;
    message?: string;
    error?: string;
    timestamp: string;
}

/** Respuesta del endpoint PUT /users/:id/modules; incluye códigos omitidos si alguno no existe o está inactivo. */
export interface AssignModulesResponse extends ApiResponse<User> {
    skippedModuleCodes?: string[];
}

@Injectable({
    providedIn: 'root',
})
export class UserService {
    private apiUrl = `${environment.apiUrl}/v1`;

    constructor(
        // eslint-disable-next-line no-unused-vars
        private http: HttpClient
    ) {}

    /**
     * Crear un nuevo usuario
     */
    /**
     * Lista de transportistas para el selector del formulario de usuario (solo admin).
     * El backend responde el sobre { success, data, ... }: se devuelve `data`.
     */
    getTransportistas(): Observable<TransportistaOption[]> {
        const url = `${this.apiUrl}/users/transportistas`;
        return this.http
            .get<ApiResponse<TransportistaOption[]>>(url, { headers: this.getAuthHeaders() })
            .pipe(map((res) => res.data ?? []));
    }

    createUser(userData: CreateUserRequest): Observable<ApiResponse<User>> {
        const url = `${this.apiUrl}/users`;
        return this.http.post<ApiResponse<User>>(url, userData, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Obtener todos los usuarios
     */
    getUsers(): Observable<ApiResponse<User[]>> {
        const url = `${this.apiUrl}/users`;
        return this.http.get<ApiResponse<User[]>>(url, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Obtener usuario por ID
     */
    getUserById(id: string): Observable<ApiResponse<User>> {
        const url = `${this.apiUrl}/users/${id}`;
        return this.http.get<ApiResponse<User>>(url, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Actualizar usuario
     */
    updateUser(
        id: string,
        userData: Partial<CreateUserRequest>
    ): Observable<ApiResponse<User>> {
        const url = `${this.apiUrl}/users/${id}`;
        return this.http.put<ApiResponse<User>>(url, userData, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Eliminar usuario
     */
    deleteUser(id: string): Observable<ApiResponse<void>> {
        const url = `${this.apiUrl}/users/${id}`;
        return this.http.delete<ApiResponse<void>>(url, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Obtener lista de clientes activos
     */
    getActiveClients(): Observable<ApiResponse<Client[]>> {
        const url = `${this.apiUrl}/clients/active`;
        return this.http.get<ApiResponse<Client[]>>(url, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Obtener lista de clientes que tienen liquidaciones
     *
     * Este método retorna solo los clientes que tienen datos de liquidaciones
     * en el sistema. Es útil para filtrar el combobox de clientes en la página
     * de liquidaciones, mostrando solo aquellos que tienen datos relevantes.
     */
    getClientsWithLiquidations(): Observable<ApiResponse<Client[]>> {
        const url = `${this.apiUrl}/clients/with-liquidations`;
        return this.http.get<ApiResponse<Client[]>>(url, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Obtener todos los módulos disponibles
     */
    getAvailableModules(): Observable<ApiResponse<Module[]>> {
        const url = `${this.apiUrl}/users/modules/available`;
        return this.http.get<ApiResponse<Module[]>>(url, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Obtener módulos asignados a un usuario
     */
    getUserModules(userId: string): Observable<ApiResponse<Module[]>> {
        const url = `${this.apiUrl}/users/${userId}/modules`;
        return this.http.get<ApiResponse<Module[]>>(url, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Asignar módulos a un usuario.
     * La respuesta puede incluir skippedModuleCodes si algún código no existe o está inactivo en el entorno.
     */
    assignModules(userId: string, moduleCodes: string[]): Observable<AssignModulesResponse> {
        const url = `${this.apiUrl}/users/${userId}/modules`;
        return this.http.put<AssignModulesResponse>(url, { moduleCodes }, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Obtener token de autenticación del localStorage
     */
    private getAuthToken(): string | null {
        return localStorage.getItem('access_token');
    }

    /**
     * Crear headers con token de autenticación
     */
    private getAuthHeaders(): HttpHeaders {
        const token = this.getAuthToken();
        return new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        });
    }
}
